import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';

import { OTHER_LCA_SOFTWARE_NAME, YES_NO } from '../../constants';
import VersionHistorySummaryGrid, { SummaryGridRow } from '../VersionHistorySummaryGrid';
import { EpdVersionHistoryData, useEpdVersionHistoryData } from '../utils/EpdVersionHistoryDataContext';
import usePcrV2 from '../../hooks/usePcrV2';

const VersionHistoryLcaInformationGrid: React.FC = () => {
  const epdVersionHistoryData = useEpdVersionHistoryData();
  const isPcrV2 = usePcrV2();

  const rows = epdVersionHistoryData ? getVersionLcaInformationRows(epdVersionHistoryData, isPcrV2) || [] : [];

  return <VersionHistorySummaryGrid rows={rows} />;
};

export default VersionHistoryLcaInformationGrid;

export const getVersionLcaInformationRows = (data: EpdVersionHistoryData, isPcrV2: boolean): SummaryGridRow[] => {
  const {
    epdDefinitionInformation,
    infrastructureAndCapitalGoods,
    lcaSpecification,
    dataSources,
    dataSourceOptions,
    lcaSoftwareOptions,
    referencePackageVersions15804,
  } = data || {};

  const possibleRows: (SummaryGridRow | false)[] = [
    {
      ord: 1,
      name: 'Are infrastructure or capital goods included in any upstream, core or downstream processes?',
      value: infrastructureAndCapitalGoods?.includedInProcesses ?? NOT_ASSIGNED,
    },
    !!infrastructureAndCapitalGoods &&
      infrastructureAndCapitalGoods.includedInProcesses === YES_NO.YES &&
      isPcrV2 && {
        ord: 2,
        name: 'Do infrastructure and capital goods contribute more than 10% to the A1-A3 (A1-A5 for services) results of any environmental impact indicator declared in the EPD?',
        value: infrastructureAndCapitalGoods.contributionLevel ?? NOT_ASSIGNED,
      },
    !!infrastructureAndCapitalGoods &&
      infrastructureAndCapitalGoods.includedInProcesses === YES_NO.YES &&
      (!isPcrV2 || infrastructureAndCapitalGoods.contributionLevel === YES_NO.YES) && {
        ord: 3,
        name: isPcrV2
          ? 'Description of infrastructure and capital goods'
          : 'Detailed description of infrastructure and capital goods',
        value: infrastructureAndCapitalGoods.description ?? NOT_ASSIGNED,
        isHtmlRichText: true,
      },
    {
      ord: 4,
      name: 'Data sources used for this EPD',
      value:
        [
          ...dataSources.map((ds) => {
            const dataSource = dataSourceOptions.find((o) => o.id === ds.dataSourceId);
            if (!dataSource) {
              return NOT_ASSIGNED;
            }
            const version = dataSource.dataSourceVersions.find((v) => v.value === ds.dataSourceVersionId);
            return `${dataSource.name}\n${version?.label ?? NOT_ASSIGNED}`;
          }),
          epdDefinitionInformation.isOtherDatabaseUsedAsDatasource
            ? `Other database\n${epdDefinitionInformation.otherDatabaseUsedAsDatasourceDescription ?? NOT_ASSIGNED}`
            : null,
        ]
          .filter(Boolean)
          .join('\n\n') || NOT_ASSIGNED,
    },
    {
      ord: 5,
      name: 'LCA Software',
      value: (() => {
        if (!lcaSpecification) {
          return NOT_ASSIGNED;
        }

        const software = lcaSoftwareOptions.find((o) => o.id === lcaSpecification.lcaSoftwareId);

        let softwareName;
        let softwareVersion;
        if (software?.name === OTHER_LCA_SOFTWARE_NAME) {
          softwareName = lcaSpecification.otherSoftwareName;
          softwareVersion = lcaSpecification.otherSoftwareVersion;
        } else {
          softwareName = software?.name;
          softwareVersion = software?.lcaSoftwareVersions.find(
            (v) => v.value.toLowerCase() === lcaSpecification.lcaSoftwareVersionId?.toLowerCase()
          )?.label;
        }

        return (
          softwareName || softwareVersion
            ? `${softwareName ? softwareName + ' ' : ''}${softwareVersion ?? ''}`
            : NOT_ASSIGNED
        ).trim();
      })(),
    },
    {
      ord: 6,
      name: 'Additional information about the underlying LCA-based information',
      value: lcaSpecification?.lcaBasedDescription ?? NOT_ASSIGNED,
      isHtmlRichText: true,
    },
    {
      ord: 7,
      name: 'Version of the EN 15804 reference package',
      value: (() => {
        if (!epdDefinitionInformation.referencePackageVersion15804) {
          return NOT_ASSIGNED;
        }
        const version = referencePackageVersions15804.versions.find(
          (v) => v.id === epdDefinitionInformation.referencePackageVersion15804
        );
        if (!version) {
          return NOT_ASSIGNED;
        }
        return version.name;
      })(),
    },
    {
      ord: 8,
      name: 'Characterisation methods',
      value: epdDefinitionInformation.characterisationMethods ?? NOT_ASSIGNED,
    },
    {
      ord: 9,
      name: 'Technology description including background system',
      value: lcaSpecification?.technologyDescription ?? NOT_ASSIGNED,
    },
  ];

  return possibleRows.filter(Boolean) as SummaryGridRow[];
};
